// src/pages/Registro.jsx
import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { auth, db } from '../firebase'; // Asegúrate de exportar auth y db desde firebase.js

const IMGBB_KEY = process.env.REACT_APP_IMGBB_KEY;

const Registro = () => {
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  // Estado para controlar el paso actual del formulario
  const [step, setStep] = useState(1);

  // Estados para los campos del formulario
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  
  // Paso 2: Datos de identificación
  const [idNumber, setIdNumber] = useState(''); // Cédula o pasaporte
  // Aquí se guardará el objeto File
  const [documentPhoto, setDocumentPhoto] = useState(null);
  const [selfie, setSelfie] = useState(null);
  
  // Paso 3: Información adicional
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState('');
  const [howDidYouKnow, setHowDidYouKnow] = useState('');

  // Estados para mensajes de error, éxito y carga
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Handler para navegar entre pasos
  const handleNext = () => {
    setError('');
    setStep(step + 1);
  };

  const handlePrev = () => {
    setError('');
    setStep(step - 1);
  };

  // Handler para el cambio en inputs de archivos (solo imágenes)
  const handleFileChange = (e, setFile) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten archivos de imagen.');
        return;
      }
      
    
      // Verificar si el archivo es una imagen y si su tamaño es menor a 1MB
      if (!file.type.startsWith('image/')) {
        alert('Solo se permiten imágenes para el comprobante.');
        return;
      }
  
      if (file.size > 1048576) { // 1MB = 1048576 bytes
        alert('El tamaño del archivo no puede ser mayor a 1 MB.');
        return;
      }
      setFile(file);
      console.log(`File selected: ${file.name}`);
    }
  };

  // Handlers para cambios en los inputs de texto
  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    console.log(`Input changed: ${e.target.id} = ${e.target.value}`);
  };

  // Handler para el submit final
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Subir imágenes a imgbb y obtener URLs
      let documentPhotoPath = null;
      if (documentPhoto) {
        const formData = new FormData();
        formData.append('image', documentPhoto);
        const res = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_KEY}`, {
          method: 'POST',
          body: formData
        });
        const { data } = await res.json();
        documentPhotoPath = data.url;
      }
      let selfiePath = null;
      if (selfie) {
        const formData2 = new FormData();
        formData2.append('image', selfie);
        const res2 = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_KEY}`, {
          method: 'POST',
          body: formData2
        });
        const { data: d2 } = await res2.json();
        selfiePath = d2.url;
      }

      // Crear usuario en Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Actualizar el displayName en Auth
      await updateProfile(userCredential.user, { displayName: `${nombre} ${apellido}` });
      
      // Preparar los datos adicionales incluyendo nombre, apellido y email
      const extraData = {
        firstName: nombre,
        lastName: apellido,
        email,
        phone,
        idNumber,
        address,
        country,
        howDidYouKnow,
        documentPhoto: documentPhotoPath,
        selfie: selfiePath,
        verificationStatus: "pendiente",
        role: 'cliente',
        createdAt: new Date().toISOString()
      };

      // Guardar los datos adicionales en Firestore (por ejemplo, en una colección "users" con el uid como ID)
      await setDoc(doc(db, "users", userCredential.user.uid), extraData, { merge: true });

      setSuccess("Cuenta creada correctamente. Tu cuenta está pendiente de verificación. Recibirás un correo cuando sea aprobada.");
      console.log("User data submitted:", { email, nombre, ...extraData });
      
      // Limpiar los campos
      setNombre('');
      setApellido('');
      setEmail('');
      setPassword('');
      setIdNumber('');
      setAddress('');
      setCountry('');
      setHowDidYouKnow('');
      setDocumentPhoto(null);
      setSelfie(null);
      setStep(1);
      
    } catch (err) {
      console.error(err);
      if (err.code) {
        switch(err.code) {
          case 'auth/invalid-email':
            setError("El correo ingresado no es válido.");
            break;
          case 'auth/email-already-in-use':
            setError("El correo ya está en uso.");
            break;
          case 'auth/weak-password':
            setError("La contraseña es demasiado débil.");
            break;
          default:
            setError("Ocurrió un error. Por favor, inténtalo de nuevo.");
        }
      } else {
        setError("Ocurrió un error inesperado. Por favor, inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Renderizado condicional según el paso
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div data-aos="fade-up">
            <h2 className="mb-4">Información Básica</h2>
            <div className="form-row">
              <div className="form-group col-md-6">
                <label htmlFor="nombre">Nombre</label>
                <input type="text" id="nombre" className="form-control" placeholder="Tu nombre" value={nombre} onChange={handleInputChange(setNombre)} required />
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="apellido">Apellido</label>
                <input type="text" id="apellido" className="form-control" placeholder="Tu apellido" value={apellido} onChange={handleInputChange(setApellido)} required />
              </div>
            </div>
            <div className="form-group mt-3">
              <label htmlFor="email">Correo Electrónico</label>
              <input type="email" id="email" className="form-control" placeholder="Tu email" value={email} onChange={handleInputChange(setEmail)} required />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="phone">Teléfono</label>
              <input type="text" id="phone" className="form-control" placeholder="Tu número de teléfono" value={phone} onChange={handleInputChange(setPhone)} required />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="password">Contraseña</label>
              <input
                type="password"
                id="password"
                className="form-control"
                placeholder="Crea una contraseña"
                value={password || ''}
                onChange={handleInputChange(setPassword)}
                required
              />
            </div>
            <div className="mt-4">
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Siguiente
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div data-aos="fade-up">
            <h2 className="mb-4">Datos de Identificación</h2>
            <div className="form-group">
              <label htmlFor="idNumber">Cédula o Pasaporte</label>
              <input
                type="text"
                id="idNumber"
                className="form-control"
                placeholder="Número de identificación"
                value={idNumber || ''}
                onChange={handleInputChange(setIdNumber)}
                required
              />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="documentPhoto">Foto del Documento</label>
              <input
                type="file"
                id="documentPhoto"
                className="form-control"
                onChange={(e) => handleFileChange(e, setDocumentPhoto)}
                required
              />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="selfie">Selfie con el Documento</label>
              <input
                type="file"
                id="selfie"
                className="form-control"
                onChange={(e) => handleFileChange(e, setSelfie)}
                required
              />
            </div>
            <div className="mt-4">
              <button type="button" className="btn btn-secondary me-2" onClick={handlePrev}>
                Anterior
              </button>
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Siguiente
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div data-aos="fade-up">
            <h2 className="mb-4">Información Adicional</h2>
            <div className="form-group">
              <label htmlFor="address">Dirección</label>
              <input
                type="text"
                id="address"
                className="form-control"
                placeholder="Tu dirección"
                value={address || ''}
                onChange={handleInputChange(setAddress)}
                required
              />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="country">País</label>
              <input
                type="text"
                id="country"
                className="form-control"
                placeholder="Tu país"
                value={country || ''}
                onChange={handleInputChange(setCountry)}
                required
              />
            </div>
            <div className="form-group mt-3">
              <label htmlFor="howDidYouKnow">¿Cómo nos conociste?</label>
              <input
                type="text"
                id="howDidYouKnow"
                className="form-control"
                placeholder="Por ejemplo: Redes Sociales, Recomendación, etc."
                value={howDidYouKnow || ''}
                onChange={handleInputChange(setHowDidYouKnow)}
                required
              />
            </div>
            <div className="mt-4">
              <button type="button" className="btn btn-secondary me-2" onClick={handlePrev}>
                Anterior
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Procesando..." : "Crear Cuenta"}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <section className="hero-section" id="hero-registro">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8" data-aos="fade-right">
              <h1>Regístrate en Smart Money</h1>
              <p className="mb-4">
                Crea tu cuenta y accede a todas las funcionalidades para enviar y recibir divisas.
              </p>
            </div>
            <div className="col-lg-4" data-aos="fade-left">
              <img src="/assets/img/register.png" alt="Registro" className="img-fluid" />
            </div>
          </div>
        </div>
      </section>
      <main id="main">
        <section className="section">
          <div className="container">
            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}
            <form onSubmit={handleSubmit} className="php-email-form">
              {renderStep()}
            </form>            
            <p className="mt-3">  
              ¿Ya tienes una cuenta? <a href="/login">Inicia Sesión aquí</a>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Registro;
